<template>
  <el-dialog
    title="租期设置"
    :visible.sync="visible"
    width="960px"
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div class="rent-period-section">
      <div class="section-header">
        <span class="section-title">{{ goodsName || '商品' }} 的租期方案</span>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="addRentPeriod">添加租期</el-button>
      </div>

      <div class="table-header" v-if="rentPeriods.length > 0">
        <span class="header-cell" style="width: 100px;">方案名称</span>
        <span class="header-cell" style="width: 200px;">租期单位</span>
        <span class="header-cell" style="width: 120px;">租期数量</span>
        <span class="header-cell" style="width: 90px;">换算天数</span>
        <span class="header-cell" style="width: 160px;">单位租金</span>
        <span class="header-cell" style="width: 60px;">操作</span>
      </div>

      <div v-for="(period, index) in rentPeriods" :key="index" class="period-row">
        <span class="period-name-display" style="width: 100px;">{{ getDefaultPeriodName(period) }}</span>
        <el-radio-group v-model="period.rentUnit" size="mini" style="width: 200px;" @change="onRentUnitChange(period)">
          <el-radio-button label="DAY">按天</el-radio-button>
          <el-radio-button label="WEEK">按周</el-radio-button>
          <el-radio-button label="MONTH">按月</el-radio-button>
        </el-radio-group>
        <el-input
          v-model="period.unitCount"
          type="number"
          placeholder="1-36"
          size="small"
          style="width: 120px;"
          @blur="onUnitCountBlur(period)"
        />
        <span class="days-display" style="width: 90px;">{{ calcPeriodDays(period) }}天</span>
        <el-input
          v-model="period.unitRent"
          :placeholder="getUnitRentLabel(period.rentUnit)"
          size="small"
          style="width: 160px;"
        >
          <template slot="prepend">¥</template>
        </el-input>
        <el-button
          type="danger"
          icon="el-icon-delete"
          size="small"
          circle
          @click="removeRentPeriod(index)"
        />
      </div>

      <div class="empty-tip" v-if="rentPeriods.length === 0">
        <i class="el-icon-info"></i>
        <span>暂无租期方案，点击上方"添加租期"按钮新增</span>
      </div>
    </div>
    
    <div slot="footer">
      <el-button size="medium" @click="visible = false">取 消</el-button>
      <el-button type="primary" size="medium" :loading="submitting" @click="handleSubmit">
        确 定
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getRentPeriods, updateRentPeriods } from '@/api/goods'

import { getRentUnitPriceText, getRentUnitShortText } from '@common/statusMap.js'

export default {
  name: 'RentPeriodForm',
  data() {
    return {
      visible: false,
      goodsId: null,
      goodsName: '',
      submitting: false,
      rentPeriods: []
    }
  },
  methods: {
    async open(goods) {
      this.visible = true
      this.goodsId = goods.id
      this.goodsName = goods.name
      try {
        const res = await getRentPeriods(goods.id)
        this.rentPeriods = (res.data || []).map(p => ({
          id: p.id,
          periodName: p.periodName || '',
          rentUnit: p.rentUnit || 'DAY',
          unitCount: p.unitCount || this.calcUnitCount(p.periodDays || 30, p.rentUnit || 'DAY'),
          unitRent: p.unitRent || p.dailyRent || '',
          periodDays: p.periodDays || 30,
          dailyRent: p.dailyRent || ''
        }))
      } catch (e) {
        console.error('获取租期方案失败', e)
      }
    },
    handleClosed() {
      this.rentPeriods = []
      this.goodsId = null
      this.goodsName = ''
    },
    getUnitRentLabel(rentUnit) {
      return getRentUnitPriceText(rentUnit) || '日租金'
    },
    getUnitLabel(rentUnit) {
      return getRentUnitShortText(rentUnit) || '天'
    },
    getDefaultPeriodName(period) {
      return this.calcPeriodDays(period) + '天'
    },
    calcUnitCount(periodDays, rentUnit) {
      if (rentUnit === 'WEEK') return Math.ceil(periodDays / 7)
      if (rentUnit === 'MONTH') return Math.ceil(periodDays / 30)
      return periodDays
    },
    calcPeriodDays(period) {
      const count = period.unitCount || 0
      if (period.rentUnit === 'WEEK') return count * 7
      if (period.rentUnit === 'MONTH') return count * 30
      return count
    },
    onRentUnitChange(period) {
      const oldDays = this.calcPeriodDays(period)
      period.unitCount = this.calcUnitCount(oldDays, period.rentUnit)
    },
    onUnitCountBlur(period) {
      let val = parseInt(period.unitCount) || 1
      if (val < 1) {
        val = 1
        this.$message.warning('租期数量不能小于1')
      } else if (val > 36) {
        val = 36
        this.$message.warning('租期数量不能超过36')
      }
      period.unitCount = val
    },
    addRentPeriod() {
      if (this.rentPeriods.length >= 36) {
        this.$message.warning('租期方案最多支持36期')
        return
      }
      this.rentPeriods.push({
        periodName: '',
        rentUnit: 'DAY',
        unitCount: 30,
        unitRent: ''
      })
    },
    removeRentPeriod(index) {
      this.rentPeriods.splice(index, 1)
    },
    async handleSubmit() {
      if (this.rentPeriods.length === 0) {
        this.$message.warning('请至少添加一个租期方案')
        return
      }

      for (let i = 0; i < this.rentPeriods.length; i++) {
        const p = this.rentPeriods[i]
        if (!p.unitRent) {
          this.$message.warning(`请填写第${i + 1}个租期方案的${this.getUnitRentLabel(p.rentUnit)}`)
          return
        }
      }

      const payload = this.rentPeriods.map(p => ({
        id: p.id,
        periodName: (p.periodName || '').trim() || this.getDefaultPeriodName(p),
        rentUnit: p.rentUnit,
        unitCount: p.unitCount,
        periodDays: this.calcPeriodDays(p),
        unitRent: p.unitRent,
        dailyRent: p.unitRent
      }))

      this.submitting = true
      try {
        await updateRentPeriods(this.goodsId, payload)
        this.$message.success('更新成功')
        this.visible = false
        this.$emit('saved')
      } catch (e) {
        console.error(e)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.rent-period-section {
  margin-top: 5px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.table-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 8px;
}

.header-cell {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.period-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  margin-bottom: 10px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
}

.period-row:hover {
  border-color: #c0c4cc;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.period-row:last-child {
  margin-bottom: 0;
}

.period-name-display {
  font-size: 13px;
  font-weight: 600;
  color: #1677ff;
}

.empty-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #909399;
  font-size: 14px;
}

.empty-tip i {
  font-size: 32px;
  margin-bottom: 8px;
  color: #c0c4cc;
}

/* 弹窗整体优化 */
.el-dialog__wrapper >>> .el-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.el-dialog__wrapper >>> .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.el-dialog__wrapper >>> .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

.el-dialog__wrapper >>> .el-dialog__body {
  padding: 20px 24px 8px;
}

.el-dialog__wrapper >>> .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.el-dialog__wrapper >>> .el-dialog__headerbtn {
  top: 20px;
  right: 20px;
}

.el-dialog__wrapper >>> .el-dialog__headerbtn .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.el-dialog__wrapper >>> .el-dialog__headerbtn .el-dialog__close:hover {
  color: #1677ff;
}

</style>
